import { DecimalPipe, formatDate, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { NgForOf } from '@angular/common';
import { TabelaComponent } from '../../shared/tabela/tabela.component';
import { CarregamentoService } from '../../services/carregamento/carregamento.service';
import { LayoutPrincipalService } from '../../layouts/layout-principal/layout-principal.service';
import {
  InformacoesEducacionaisService,
  LocalizacaoService
} from '../../services';
import { InformacoesEducacionaisEscolasDto, InformacoesEducacionaisMatriculasDto } from '../../dtos/informacoes-educacionais-dto';
import { InformacoesSocioDemograficasDto } from '../../dtos/informacoes-socio-demograficas-dto';
import { QuintisDadosSocioeconomicosDto } from '../../dtos/quintis-socioeconomicos-dto';
import { InformacoesSocioDemograficasService } from '../../services/informacoes-sociodemograficas/informacoes-socio-demograficas.service';
import { QuintisSocioeconomicosService } from '../../services/quintis-socioeconomicos/quintis-socioeconomicos.service';
import { LayoutIndicadoresService } from '../../layouts/layout-indicadores/layout-indicadores.service';
import { LocalizacaoSelecionada } from '../../interfaces/location.interface';
import { TipoSimulacao } from '../../enums/simulation-type.enum';
import { HeaderInfo, PdfUtils } from '../../utils/pdf-utils';
import { ToastrService } from 'ngx-toastr';

type Dependencia =
  | 'Federal'
  | 'Estadual'
  | 'Municipal'
  | 'Privada conveniada sem fins lucrativos'
  | 'Privada conveniada com fins lucrativos'
  | 'Privada não conveniada sem fins lucrativos'
  | 'Privada não conveniada com fins lucrativos';

type Localizacao = 'Urbana' | 'Rural';

interface OrdenacaoTabela {
  campo: string;
  direcao: 'asc' | 'desc';
}

@Component({
  selector: 'app-indicadores-locais',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    TabelaComponent,
    FormsModule,
    DecimalPipe
  ],
  templateUrl: './indicadores-locais.component.html',
  styleUrl: './indicadores-locais.component.scss',
})
export class IndicadoresLocaisComponent implements OnInit {
  dadosEducacionaisMatriculas: InformacoesEducacionaisMatriculasDto[] = [];
  dadosEducacionaisEscolas: InformacoesEducacionaisEscolasDto[] = [];

  socioDemograficas?: InformacoesSocioDemograficasDto;

  quintisDadosSocioeconomicos: QuintisDadosSocioeconomicosDto[] = [];
  quintilMunicipio?: { dscClassificacao: string; vlrQuintil: number | null };

  localizacaoSelecionada?: LocalizacaoSelecionada | null;
  ehMunicipioSelecionado = false;
  ehEstadoSelecionado = false;

  ordemTabelaEscolas: OrdenacaoTabela | null = null;
  ordemTabelaMatriculas: OrdenacaoTabela | null = null;

  private readonly dependenciasPadrao: Dependencia[] = [
    'Federal',
    'Estadual',
    'Municipal',
    'Privada conveniada sem fins lucrativos',
    'Privada conveniada com fins lucrativos',
    'Privada não conveniada sem fins lucrativos',
    'Privada não conveniada com fins lucrativos'
  ];

  constructor(
    private carregamentoService: CarregamentoService,
    private layoutPrincipalService: LayoutPrincipalService,
    private informacoesEducacionaisService: InformacoesEducacionaisService,
    private informacoesSocioDemograficasService: InformacoesSocioDemograficasService,
    private quintisSocioeconomicosService: QuintisSocioeconomicosService,
    private localizacaoService: LocalizacaoService,
    private layoutIndicadoresService: LayoutIndicadoresService,
    private toastr: ToastrService
  ) { }

  async ngOnInit(): Promise<void> {
    this.layoutPrincipalService.definaMensagensCarregamento([
      'Obtendo dados educacionais',
      'Obtendo dados socioeconômicos'
    ]);

    this.localizacaoSelecionada = this.localizacaoService.obterLocalizacaoSelecionada();
    if (!this.localizacaoSelecionada) {
      this.exibirToastErro('Localização não selecionada!');
      return;
    }

    this.ehMunicipioSelecionado = !!this.localizacaoSelecionada?.cidadeSelecionada?.value;
    this.ehEstadoSelecionado = !!this.localizacaoSelecionada?.estadoSelecionado?.value && !this.localizacaoSelecionada?.cidadeSelecionada?.value;

    this.definaConfiguracoesPagina();

    try {
      this.carregamentoService.startLoading();

      const promises: Promise<any>[] = [];

      promises.push(this.carregarInformacoesEducacionais());
      promises.push(this.carregarInformacoesSocioDemograficas());

      // Quintis somente se NÃO for município
      if (!this.ehMunicipioSelecionado) {
        promises.push(this.carregarQuintis());
      }

      await Promise.all(promises);

      // Se for município, extrai o quintilMunicipio do bloco sócio-demográfico
      if (this.ehMunicipioSelecionado && this.socioDemograficas) {
        this.quintilMunicipio = {
          dscClassificacao: this.socioDemograficas.nivelProdutoInternoBruto?.dscClassificacao ?? '—',
          vlrQuintil: this.socioDemograficas.vlrQuintil ?? null
        };
      }

    } finally {
      this.carregamentoService.finishLoading();
    }
  }

  private async carregarInformacoesEducacionais(): Promise<void> {
    const [matricula, escola] = await Promise.all([
      this.informacoesEducacionaisService.buscarNumeroMatriculasLocalizacao(),
      this.informacoesEducacionaisService.buscarNumeroEscolasLocalizacao()
    ]);
    this.dadosEducacionaisMatriculas = matricula;
    this.dadosEducacionaisEscolas = escola;
  }

  private async carregarInformacoesSocioDemograficas(): Promise<void> {
    this.socioDemograficas = await this.informacoesSocioDemograficasService.buscarPorLocalizacao();
  }

  private async carregarQuintis(): Promise<void> {
    this.quintisDadosSocioeconomicos = await this.quintisSocioeconomicosService.buscarPorLocalizacao();
  }

  get deveExibirQuintilMunicipio(): boolean {
    return this.ehMunicipioSelecionado;
  }

  somarEscolas(dep: Dependencia, loc: Localizacao): number {
    return (this.dadosEducacionaisEscolas || [])
      .filter(i => i.dependenciaAdministrativa === dep && i.localizacao === loc)
      .reduce((acc, i) => acc + (i.numeroEscolas ?? 0), 0);
  }

  calcularTotalEscolas(loc: Localizacao): number {
    return (this.dadosEducacionaisEscolas || [])
      .filter(i => i.localizacao === loc)
      .reduce((acc, i) => acc + (i.numeroEscolas ?? 0), 0);
  }

  somarMatriculas(dep: Dependencia, loc: Localizacao): number {
    return (this.dadosEducacionaisMatriculas || [])
      .filter(i => i.dependenciaAdministrativa === dep && i.localizacao === loc)
      .reduce((acc, i) => acc + (i.numeroMatriculas ?? 0), 0);
  }

  calcularTotalMatriculas(loc: Localizacao): number {
    return (this.dadosEducacionaisMatriculas || [])
      .filter(i => i.localizacao === loc)
      .reduce((acc, i) => acc + (i.numeroMatriculas ?? 0), 0);
  }

  private definaConfiguracoesPagina(): void {
    this.layoutIndicadoresService.setAno('2026');
    this.layoutIndicadoresService.setPais('Brasil');
    if (this.localizacaoSelecionada?.estadoSelecionado) {
      this.layoutIndicadoresService.setEstado(this.localizacaoSelecionada.estadoSelecionado.label);
      this.layoutIndicadoresService.setSiglaEstado(this.localizacaoSelecionada.estadoSelecionado.sigla);
    }
    if (this.localizacaoSelecionada?.cidadeSelecionada?.label) {
      this.layoutIndicadoresService.setCidade(this.localizacaoSelecionada.cidadeSelecionada.label);
    }
    const planoSelecionado = this.localizacaoService
      .obterOpcoesPlanoEducacional(this.localizacaoSelecionada!)
      .find(opcao => opcao.value === this.localizacaoSelecionada?.tipoSimulacaoSelecionado);

    if (planoSelecionado?.label) {
      this.layoutIndicadoresService.setRedeSelecionada(planoSelecionado.label);
    }

    this.layoutIndicadoresService.setTextoComoCitar(
      `SIMULADOR de Custo-Aluno Qualidade: Valores do Custo-Aluno Qualidade para o Brasil, 2026. Disponível em: <https://simcaq.ufg.br/>. Acesso em: ${formatDate(new Date(),
        'dd MMM yyyy', 'pt-BR')}.`);

    this.layoutIndicadoresService.setRotaDownloadIndicadores(
      {
        tipo: 'acao',
        acao: () => this.baixarPdfPaginaCompleta()
      }
    );

    this.layoutIndicadoresService.setImagemFundo(this.definirImagemFundo());
  }

  private definirImagemFundo(): string {
    const tipo = this.localizacaoSelecionada?.tipoSimulacaoSelecionado;
    const temCidade = !!this.localizacaoSelecionada?.cidadeSelecionada;
    const temEstado = !!this.localizacaoSelecionada?.estadoSelecionado;

    if (tipo === TipoSimulacao.planejamentoNacional || (!temEstado && !temCidade)) {
      return 'assets/ilustrations/Maps/Estado=BR, Size=SM, Stroke=True.svg';
    }
    if (temEstado && !temCidade) {
      const uf = this.localizacaoSelecionada!.estadoSelecionado!.sigla;
      return `assets/ilustrations/Maps/Estado=${uf}, Size=SM, Stroke=True.svg`;
    }
    if (temCidade) {
      const uf = this.localizacaoSelecionada!.estadoSelecionado?.sigla || 'BR';
      return `assets/ilustrations/Municípios/Estado=${uf}, Size=SM.svg`;
    }

    return 'assets/ilustrations/bridge-mountain.svg';
  }

  // Ordenação da tabela de Escolas
  // Ciclo: ascendente - descentende - padrão
  ordenarTabelaEscolas(campo: string): void {
    if (this.ordemTabelaEscolas?.campo === campo) {
      if (this.ordemTabelaEscolas.direcao === 'asc') {
        this.ordemTabelaEscolas.direcao = 'desc';
      } else if (this.ordemTabelaEscolas.direcao === 'desc') {
        this.ordemTabelaEscolas = null;
      }
    } else {
      this.ordemTabelaEscolas = { campo, direcao: 'asc' };
    }
  }

  obterIconeOrdenacaoEscolas(campo: string): string {
    if (this.ordemTabelaEscolas?.campo !== campo) {
      return 'assets/icons/order-default.svg';
    }
    return this.ordemTabelaEscolas.direcao === 'asc'
      ? 'assets/icons/order-ascending.svg'
      : 'assets/icons/order-descending.svg';
  }

  private getValorEscolas = (dep: Dependencia, campo: String) => {
    if (campo === 'urbana') {
      return this.somarEscolas(dep, 'Urbana');
    }
    if (campo === 'rural') {
      return this.somarEscolas(dep, 'Rural');
    }
    return this.somarEscolas(dep, 'Urbana') + this.somarEscolas(dep, 'Rural');
  };

  obterDependenciasOrdenadasEscolas(): Dependencia[] {
    if (!this.ordemTabelaEscolas) return [...this.dependenciasPadrao];

    const { campo, direcao } = this.ordemTabelaEscolas;
    const fator = direcao === 'asc' ? 1 : -1;

    return [...this.dependenciasPadrao].sort((a, b) =>
      (this.getValorEscolas(a, campo) - this.getValorEscolas(b, campo)) * fator
    );
  }

  // Ordenação da tabela de matrículas
  // Ciclo: ascendente - descentende - padrão
  ordenarTabelaMatriculas(campo: string): void {
    if (this.ordemTabelaMatriculas?.campo === campo) {
      if (this.ordemTabelaMatriculas.direcao === 'asc') {
        this.ordemTabelaMatriculas.direcao = 'desc';
      } else if (this.ordemTabelaMatriculas.direcao === 'desc') {
        this.ordemTabelaMatriculas = null;
      }
    } else {
      // Nova coluna a ser ordenada, começa com ascendente
      this.ordemTabelaMatriculas = { campo, direcao: 'asc' };
    }
  }

  obterIconeOrdenacaoMatriculas(campo: string): string {
    if (this.ordemTabelaMatriculas?.campo !== campo) {
      return 'assets/icons/order-default.svg';
    }

    return this.ordemTabelaMatriculas.direcao === 'asc'
      ? 'assets/icons/order-ascending.svg'
      : 'assets/icons/order-descending.svg';
  }

  obterDependenciasOrdenadasMatriculas(): Dependencia[] {
    if (!this.ordemTabelaMatriculas) return [...this.dependenciasPadrao];

    const { campo, direcao } = this.ordemTabelaMatriculas;
    const fator = direcao === 'asc' ? 1 : -1;

    return [...this.dependenciasPadrao].sort((a, b) =>
      (this.getValorMatriculas(a, campo) - this.getValorMatriculas(b, campo)) * fator
    );
  }

  private getValorMatriculas = (dep: Dependencia, campo: String) => {
    if (campo === 'urbana') {
      return this.somarMatriculas(dep, 'Urbana');
    }
    if (campo === 'rural') {
      return this.somarMatriculas(dep, 'Rural');
    }
    return this.somarMatriculas(dep, 'Urbana') + this.somarMatriculas(dep, 'Rural');
  };

  async baixarPdfInformacoesSociodemograficas(): Promise<void> {
    await this.gerarPdf('informacoes-sociodemograficas', 'informacoes_sociodemograficas', 'Indicadores');
  }

  async baixarPdfInformacoesEducacionais(): Promise<void> {
    await this.gerarPdf('informacoes-educacionais', 'informacoes_educacionais', 'Indicadores');
  }

  async baixarPdfPaginaCompleta(): Promise<void> {
    await this.gerarPdf('container', 'indicadores', 'Indicadores');
  }

  private async gerarPdf(containerId: string, nomeArquivo: string, titulo: string): Promise<void> {
    const container = document.getElementById(containerId) as HTMLElement;

    if (!container) {
      console.error(`Container ${containerId} não encontrado`);
      return;
    }

    try {
      // Salvar estado original
      const paddingOriginal = container.style.padding;

      // Ocultar ícones de ordenação
      const sortIcons = container.querySelectorAll('.sort-icon, .sort-container img');
      const iconsData: { element: HTMLElement, originalDisplay: string }[] = [];

      sortIcons.forEach((icon: Element) => {
        const iconElement = icon as HTMLElement;
        iconsData.push({
          element: iconElement,
          originalDisplay: iconElement.style.display || 'block'
        });
        iconElement.style.display = 'none';
      });

      // Aplicar padding
      container.style.padding = '32px';

      const localizacao = this.obterTextoLocalizacao();
      const headerInfo = this.criarHeaderInfoIndicadores(titulo);

      await PdfUtils.baixarElementoComoPdf(
        container,
        nomeArquivo,
        'Indicadores',
        localizacao,
        '2025',
        headerInfo
      );

      // Restaurar estado original
      container.style.padding = paddingOriginal;

      // Restaurar ícones
      iconsData.forEach(iconData => {
        iconData.element.style.display = iconData.originalDisplay;
      });

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      // Restaurar mesmo em caso de erro
      container.style.padding = '';
      const sortIcons = container.querySelectorAll('.sort-icon, .sort-container img');
      sortIcons.forEach((icon: Element) => {
        (icon as HTMLElement).style.display = '';
      });
    }
  }

  private obterTextoLocalizacao(): string {
    const localizacao = this.localizacaoSelecionada;

    if (localizacao?.cidadeSelecionada) {
      return `${localizacao.cidadeSelecionada.label} - ${localizacao.estadoSelecionado?.sigla}`;
    }

    if (localizacao?.estadoSelecionado) {
      return localizacao.estadoSelecionado.label || localizacao.estadoSelecionado.sigla;
    }

    return 'Brasil';
  }

  // Método para criar header info dos indicadores
  private criarHeaderInfoIndicadores(titulo: string): HeaderInfo {
    const localizacao = this.localizacaoSelecionada;
    const tipo = localizacao?.tipoSimulacaoSelecionado;
    let ilustracao = this.definirImagemFundo();

    return {
      titulo: titulo,
      tituloModo: 'Indicadores',
      subtitulo: 'Indicadores sociais, educacionais e financeiros da localidade',
      ilustracao: ilustracao,
      breadcrumb: {
        plano: this.localizacaoSelecionada?.planoEducacionalSelecionado?.label || 'Plano não selecionado',
        pais: 'Brasil',
        estado: localizacao?.estadoSelecionado?.sigla || 'BR',
        cidade: localizacao?.cidadeSelecionada?.label,
        ano: '2026'
      }
    };
  }

  private exibirToastErro(mensagens: string): void {
    this.toastr.error(mensagens, 'Erro!');
  }
}
